import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Eraser, Loader2 } from "lucide-react";

export default function TidyBookDialog({ open, onOpenChange, projectId, onBeforeStart, onDone }) {
  const [est, setEst] = useState(null);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const poll = useRef(null);

  const stopPoll = () => { clearInterval(poll.current); poll.current = null; };
  const startPoll = () => {
    stopPoll();
    poll.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/tidy-book/status`);
        setJob(data);
        if (data.status !== "running") {
          stopPoll();
          if (data.status === "done") toast.success(`Book tidied: ${data.changed_chapters} chapter${data.changed_chapters === 1 ? "" : "s"} improved (${data.credits} credits). Old versions are in each chapter's History.`);
          else if (data.error) toast.error(data.error);
          onDone?.();
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  useEffect(() => {
    if (!open) { stopPoll(); return; }
    setEst(null); setJob(null);
    api.get(`/projects/${projectId}/tidy-book/status`).then(({ data }) => {
      if (data.status === "running") { setJob(data); startPoll(); }
    }).catch(() => {});
    api.get(`/projects/${projectId}/tidy-book/estimate`).then(({ data }) => setEst(data))
      .catch(() => toast.error("Couldn't scan the book — please try again"));
    return stopPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  const start = async () => {
    setStarting(true);
    try {
      await onBeforeStart?.();
      await api.post(`/projects/${projectId}/tidy-book`);
      setJob({ status: "running", done: 0, total: est?.chapters_to_tidy || 0 });
      startPoll();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error === "out_of_credits" ? `Not enough credits (need ${d.needed})` : "Couldn't start the tidy"));
    } finally { setStarting(false); }
  };

  const running = job?.status === "running";
  const pct = job?.total ? Math.round((job.done / job.total) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover max-w-lg" data-testid="tidy-book-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Eraser className="w-4 h-4" /> Tidy repetition across the book</DialogTitle>
          <DialogDescription>Fixes only the paragraphs that overuse phrases or sentence habits. Everything else stays word-for-word, and each chapter's old version is saved to History.</DialogDescription>
        </DialogHeader>

        {!est && !running && <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="tidy-book-scanning"><Loader2 className="w-4 h-4 animate-spin" /> Scanning the book…</div>}

        {est && !running && job?.status !== "done" && (
          <div className="space-y-3 text-sm">
            <div className="rounded-lg border border-border bg-secondary/30 px-3 py-2" data-testid="tidy-book-estimate">
              <span className="font-medium">{est.chapters_to_tidy}</span> of {est.chapters_written} written chapters need tidying
              <span className="float-right font-medium">{est.est_credits} credits max</span>
            </div>
            {est.overused.length > 0 && (
              <div data-testid="tidy-book-overused">
                <div className="text-xs font-medium text-muted-foreground mb-1">Overused across the whole book</div>
                <ul className="text-xs space-y-0.5">
                  {est.overused.map((o) => <li key={o.label} className="flex justify-between gap-3"><span className="min-w-0 truncate">{o.label}</span><span className="text-muted-foreground shrink-0">{o.count}×</span></li>)}
                </ul>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">2 credits per chapter, only charged when something is actually changed.</p>
            <Button className="w-full" onClick={start} disabled={starting || est.chapters_to_tidy < 1} data-testid="tidy-book-start">
              {starting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eraser className="w-4 h-4 mr-2" />}
              {est.chapters_to_tidy < 1 ? "Nothing to tidy" : `Tidy ${est.chapters_to_tidy} chapter${est.chapters_to_tidy === 1 ? "" : "s"}`}
            </Button>
          </div>
        )}

        {(running || job?.status === "done" || job?.status === "error") && (
          <div className="space-y-2 text-sm" data-testid="tidy-book-progress">
            <Progress value={job?.status === "done" ? 100 : pct} />
            <div className="text-xs text-muted-foreground">
              {running ? `Tidying${job.current ? ` “${job.current}”` : ""}… ${job.done || 0} of ${job.total || 0}` :
                job.status === "done" ? `Done: ${job.changed_chapters} chapter(s) improved, ${job.credits} credits used.` : job.error}
            </div>
            {!running && <Button variant="outline" className="w-full" onClick={() => onOpenChange(false)} data-testid="tidy-book-close">Close</Button>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
